// =============================================================================
// OAuth Callback Promise
//
// Registers a Hub listener at MODULE LOAD TIME — before Amplify.configure()
// and before React renders — so the signInWithRedirect / signInWithRedirect_failure
// Hub event is NEVER missed regardless of when the Cognito token exchange
// completes relative to React's useEffect scheduling.
//
// Why this is necessary:
//   Amplify.configure() kicks off attemptCompleteOAuthFlow() as a fire-and-
//   forget async task. On fast networks, the Cognito token exchange can complete
//   and fire the Hub event BEFORE React has run any useEffect (which is where
//   components normally register Hub listeners). A missed Hub event cannot be
//   replayed, leaving the callback page stuck forever.
//
// This module is transitively imported by AuthCallbackPage → App → main.tsx,
// so the listener is always in place before Amplify.configure() runs.
// =============================================================================
import { Hub } from 'aws-amplify/utils';

export type OAuthCallbackEvent = 'signInWithRedirect' | 'signInWithRedirect_failure';

export interface OAuthCallbackResult {
  event: OAuthCallbackEvent;
  /** Error detail from Amplify — only present on signInWithRedirect_failure. */
  errorMessage?: string;
}

/**
 * Resolves with the first OAuth Hub event that fires for this page load.
 * Since this module is a singleton, the promise resolves at most once per
 * full page load — safe for the single login-flow use case.
 */
export const oauthCallbackPromise = new Promise<OAuthCallbackResult>((resolve) => {
  const unsubscribe = Hub.listen('auth', ({ payload }) => {
    if (payload.event === 'signInWithRedirect') {
      unsubscribe();
      resolve({ event: 'signInWithRedirect' });
    } else if (payload.event === 'signInWithRedirect_failure') {
      unsubscribe();
      resolve({
        event: 'signInWithRedirect_failure',
        errorMessage: payload.data?.error?.message ?? 'Unknown error',
      });
    }
  });
});
